import { Link } from "react-router-dom";
import {
    ShieldCheck, FolderLock, Siren, Sparkles, Briefcase, Users, ArrowRight,
    Scroll, BookHeart, MessageCircle, Heart, Cpu, Globe2,
    Library, TrendingUp, Network, Lock, Target, Layers, Handshake,
} from "lucide-react";
import { BrandLockup, MythosBrand } from "@/components/Brand";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/46550ece-39b8-4078-b7c9-54da2819ff30/images/f8020ad3bc2b29a3986b3108cd0f2da7fc3edabbbfcfcf4c503f978d8cce07b0.png";
const ADVISOR_IMG = "https://static.prod-images.emergentagent.com/jobs/46550ece-39b8-4078-b7c9-54da2819ff30/images/a50d0f3aff0e596a026feddd5301ffe72909df41f1bfa60b3391f304ff088e9b.png";
const SHIELD_IMG = "https://static.prod-images.emergentagent.com/jobs/46550ece-39b8-4078-b7c9-54da2819ff30/images/e9c1df28b695bb944583e0287d4dfe320fe6e0e0dfe7130b1c94a2c45b3412c2.png";
const FAMILY_IMG = "https://static.prod-images.emergentagent.com/jobs/46550ece-39b8-4078-b7c9-54da2819ff30/images/b10dc954ce23f6ee50c38d119bd25a43c4fe06f60bbdaf354cf6f437ed9741fa.png";

const whyNow = [
    {
        icon: TrendingUp, k: "₹128 lakh-crore",
        l: "in Indian household wealth changing hands over the next 25 years — the largest generational transfer in our history.",
    },
    {
        icon: Cpu, k: "AI inflection",
        l: "First time consumer AI is good enough to read a Will, summarise a portfolio, and surface a missing nominee — at family-affordable cost.",
    },
    {
        icon: Network, k: "250,000 advisors",
        l: "registered in India, all manually maintaining client paperwork. Zero AI-native software built for them. Built-in distribution.",
    },
];

const moats = [
    { icon: Layers, t: "Data graph moat", b: "Documents + finances + nominees + relationships in one schema. Every active vault deepens the graph and AI quality compounds." },
    { icon: Network, t: "Network effect — Crisis Mode", b: "Every shared QR PIN turns a family member into a future user. Inheritance is built-in virality." },
    { icon: Handshake, t: "B2B2C via advisors", b: "Advisors onboard their own books at ₹500–2,000/client/yr. Zero CAC, instant trust transfer, recurring revenue." },
    { icon: Library, t: "Parent IP — Mythos India", b: "EduSphere + LifeVault share infra, brand, and AI stack. One studio, multiple platform plays for the same household." },
];

const pillars = [
    { icon: BookHeart, title: "Living Archive", body: "Stories, memories, family heritage — preserved as code, not just paper." },
    { icon: FolderLock, title: "Document Vault", body: "PAN, Aadhaar, Will, deeds — encrypted, classified by AI vision." },
    { icon: ShieldCheck, title: "Material Legacy", body: "Insurance, investments, deposits, assets — clear, current, claimable." },
    { icon: Scroll, title: "Legacy Plans", body: "Wills, POAs, nominee maps — your wishes, machine-readable." },
    { icon: Users, title: "Family Network", body: "The people behind it all — relations, professionals, executors." },
    { icon: Siren, title: "Sanctuary Mode", body: "One-click crisis access — shareable QR, PIN-gated, audit-logged." },
];

const aiPillars = [
    { icon: MessageCircle, title: "Ask LifeVault", body: "A private assistant grounded only in the user's vault. Never trained on customer data." },
    { icon: Sparkles, title: "Vault Score", body: "Quarterly AI diagnostic — strengths, gaps, three actions. Habit-forming retention loop." },
    { icon: Heart, title: "Legacy Rehearsal", body: "Narrative simulation of a hand-over event. Exposes the missing nominee before it costs the family." },
];

const metrics = [
    { v: "$7.8T", l: "Indian household wealth" },
    { v: "10M+", l: "HNI households (target)" },
    { v: "₹500–2k", l: "ARPU per client / year" },
    { v: "30%+", l: "Annual retention modelled" },
    { v: "0 CAC", l: "Advisor-led GTM" },
    { v: "11", l: "Module surfaces shipped" },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Nav */}
            <header className="border-b border-border/70 backdrop-blur-md bg-background/80 sticky top-0 z-30">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 py-4 flex items-center justify-between">
                    <Link to="/" data-testid="brand-logo">
                        <BrandLockup size="sm" parent={true} />
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm">
                        <a href="#thesis" className="text-muted-foreground hover:text-foreground">Thesis</a>
                        <a href="#moat" className="text-muted-foreground hover:text-foreground">Moat</a>
                        <a href="#product" className="text-muted-foreground hover:text-foreground">Product</a>
                        <a href="#mythos" className="text-muted-foreground hover:text-foreground">Mythos India</a>
                        <Link to="/investors" data-testid="nav-investors" className="text-muted-foreground hover:text-foreground">Investors</Link>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login" data-testid="nav-login" className="text-sm text-foreground/80 hover:text-foreground px-3 py-2">
                            Sign in
                        </Link>
                        <Link
                            to="/register"
                            data-testid="nav-register"
                            className="text-sm bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/90 transition-colors duration-200 font-semibold"
                        >
                            Begin your vault
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO — VC bait */}
            <section className="relative aurora overflow-hidden border-b border-border/70 grain">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
                    <div className="lg:col-span-7">
                        <a
                            href="https://mythosindiastudio.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-mythos-tag"
                            className="label-tag mb-5 inline-flex items-center gap-2 hover:text-foreground transition-colors"
                        >
                            <img
                                src="/brand/mythos_color_logo.png"
                                alt=""
                                aria-hidden="true"
                                width={18}
                                height={18}
                                className="object-contain"
                                style={{ width: 18, height: 18 }}
                            />
                            A MYTHIVA™ platform · AI-first · Series-ready ↗
                        </a>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tighter">
                            India's <span className="font-mono text-primary">$7.8T</span>
                            <br />
                            household wealth
                            <br />
                            has <span className="italic font-semibold opacity-80">no</span> operating system.
                            <br />
                            <span className="text-primary">We're shipping it.</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            LifeVault is the AI life-OS for the Indian household — documents,
                            finances, heritage, nominees, advisors, all in one inheritable
                            graph. Backed by MYTHIVA™.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link
                                to="/investors"
                                data-testid="hero-investors"
                                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors duration-200"
                            >
                                Investor brief <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/register"
                                data-testid="hero-get-started"
                                className="inline-flex items-center gap-2 border border-border bg-card/60 backdrop-blur px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-card transition-colors duration-200"
                            >
                                See the product
                            </Link>
                        </div>
                        <div className="mt-12 flex items-center gap-8 text-xs uppercase tracking-[0.28em] font-semibold text-muted-foreground">
                            <span className="inline-flex items-center gap-2"><Cpu className="h-3.5 w-3.5" /> AI-first</span>
                            <span className="inline-flex items-center gap-2"><Globe2 className="h-3.5 w-3.5" /> India-rooted</span>
                            <span className="inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> SOC-grade</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="absolute -inset-8 rounded-3xl bg-primary/10 blur-3xl" />
                        <div className="relative rounded-2xl border border-border overflow-hidden">
                            <img src={HERO_IMG} alt="LifeVault by MYTHIVA™" className="w-full object-cover aspect-[4/5]" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-background/70 via-transparent to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                                <span className="label-tag">Encrypted · Generational</span>
                                <BrandLockup size="sm" parent={false} className="text-primary-foreground" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Metrics strip */}
                <div className="border-t border-border/70 bg-card/30 backdrop-blur">
                    <div className="mx-auto max-w-7xl px-6 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {metrics.map((m) => (
                            <div key={m.l} data-testid={`metric-${m.l.replace(/\s+/g, '-').toLowerCase()}`}>
                                <div className="font-display text-2xl lg:text-3xl font-extrabold font-mono tracking-tight text-primary">{m.v}</div>
                                <div className="mt-1 label-tag">{m.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY NOW */}
            <section id="thesis" className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-5">
                            <div className="label-tag mb-3">Why now</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                                Three curves<br />
                                <span className="italic font-semibold opacity-70">are crossing.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-7 text-muted-foreground text-base sm:text-lg leading-relaxed self-end">
                            The largest wealth transfer in India's history is colliding with consumer-grade AI
                            and a regulator-led push to formalise advisory. The window to win the household
                            operating system is narrow — and entirely open.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {whyNow.map((w) => (
                            <article key={w.k} className="rounded-lg border border-border bg-card p-7" data-testid={`whynow-${w.k.replace(/\s+/g, '-')}`}>
                                <w.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                <div className="mt-5 font-display text-3xl font-bold tracking-tight">{w.k}</div>
                                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{w.l}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* MOAT */}
            <section id="moat" className="py-20 lg:py-28 border-y border-border/70 bg-card/30">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-5">
                            <div className="label-tag mb-3">Defensibility</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                                Four moats. <br />
                                <span className="italic font-semibold opacity-70">Compounding.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-7 text-muted-foreground text-base sm:text-lg leading-relaxed self-end">
                            Every active household widens our data graph, every advisor brings their own
                            book at zero CAC, every Sanctuary share recruits a future user. This is not
                            a SaaS — it's a generational protocol with built-in viral mechanics.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {moats.map((m) => (
                            <article key={m.t} className="module-card" data-testid={`moat-${m.t.replace(/\s+/g, '-').toLowerCase()}`}>
                                <m.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-5 font-display text-xl font-semibold">{m.t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.b}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRODUCT — pillars */}
            <section id="product" className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-5">
                            <div className="label-tag mb-3">The Product</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                                Six pillars.<br />
                                <span className="italic font-semibold opacity-70">One graph.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-7 text-muted-foreground text-base sm:text-lg leading-relaxed self-end">
                            LifeVault doesn't pick between finance, heritage, or emergency.
                            It binds them. Authored by you, augmented by AI, accessible to those you choose.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {pillars.map((m) => (
                            <article key={m.title} data-testid={`pillar-${m.title.replace(/\s+/g, '-').toLowerCase()}`} className="module-card">
                                <m.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-5 font-display text-xl font-semibold">{m.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI BAND */}
            <section className="py-20 lg:py-24 border-t border-border/70">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="label-tag mb-3 justify-center inline-flex">AI-first by design</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
                            An assistant that reads only <span className="text-primary italic">you</span>.
                        </h2>
                        <p className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                            Three AI surfaces, all grounded in private vault snapshots.
                            Built on Claude Sonnet 4.5 — never trained on customer data.
                        </p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {aiPillars.map((p) => (
                            <article key={p.title} data-testid={`ai-pillar-${p.title.replace(/\s+/g, '-').toLowerCase()}`} className="module-card">
                                <p.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-5 font-display text-xl font-semibold">{p.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* SANCTUARY */}
            <section className="border-t border-border/70 grain"
                style={{ background: "hsl(12 30% 7%)", color: "hsl(36 35% 96%)" }}>
                <div className="mx-auto max-w-7xl px-6 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <img src={SHIELD_IMG} alt="Sanctuary Mode" className="w-full rounded-2xl border border-white/10" />
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <div className="text-xs uppercase tracking-[0.28em] font-semibold text-orange-400 mb-4 inline-flex items-center gap-2">
                            <Siren className="h-4 w-4" /> Sanctuary Mode · The viral wedge
                        </div>
                        <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            Every share <br />
                            <span className="italic opacity-80">is a future user.</span>
                        </h2>
                        <p className="mt-6 text-white/70 text-base leading-relaxed max-w-xl">
                            A signed, PIN-protected emergency view shared as a QR on a wallet card.
                            On the day it's needed, a family member scans it, enters the PIN, and
                            sees the only ten things that matter — instantly. Inheritance becomes
                            distribution.
                        </p>
                        <div className="mt-8 flex items-center gap-8 text-xs uppercase tracking-[0.28em] font-semibold text-white/60">
                            <span>Built-in K-factor</span>
                            <span>Audit-logged</span>
                            <span>Brute-force-protected</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* GTM — advisor */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <div className="label-tag mb-3">GTM · Advisor-led</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
                            250,000 advisors. <br />
                            <span className="text-primary">Captive distribution.</span>
                        </h2>
                        <p className="mt-5 text-muted-foreground leading-relaxed text-base">
                            Indian financial advisors already manage paperwork for 10–500
                            clients each. We give them an AI-native console, charge ₹500–2,000
                            per client per year, and they bring their own book at zero CAC.
                        </p>
                        <div className="mt-8 grid sm:grid-cols-3 gap-6">
                            {[
                                ["Avg. book", "120 clients"],
                                ["ARPU", "₹2,000 / yr"],
                                ["Per advisor", "₹2.4L ARR"],
                            ].map(([k, v]) => (
                                <div key={k}>
                                    <div className="font-display text-xl font-bold font-mono">{v}</div>
                                    <div className="label-tag mt-1">{k}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8">
                            <Briefcase className="inline h-5 w-5 text-primary mr-2" />
                            <span className="font-mono text-sm">
                                10,000 advisors @ ARPU = ₹240 Cr ARR · before any DTC household.
                            </span>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <img src={ADVISOR_IMG} alt="Advisor desk" className="w-full rounded-2xl border border-border" />
                    </div>
                </div>
            </section>

            {/* MYTHOS PARENT — platform thesis */}
            <section id="mythos" className="py-20 lg:py-28 border-y border-border/70 bg-card/30">
                <div className="mx-auto max-w-7xl px-6 sm:px-8">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <MythosBrand size="lg" showCIN={true} testid="landing-mythos-brand" />
                            <h2 className="mt-8 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
                                One studio.<br />
                                <span className="italic opacity-70">Two platforms.</span>
                                <br />
                                The same household.
                            </h2>
                            <p className="mt-5 text-muted-foreground leading-relaxed text-base max-w-md">
                                MYTHIVA™ builds AI-first cultural technology — intelligent
                                storytelling, immersive education, digital heritage preservation. LifeVault
                                is the wealth/heritage layer. EduSphere is the learning layer. Same family,
                                same brand, same AI stack — compounding LTV per household.
                            </p>
                            <a
                                href="https://mythosindiastudio.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="visit-mythos-cta"
                                className="mt-6 inline-flex items-center gap-2 border border-border bg-card px-5 py-3 rounded-full text-sm font-semibold hover:bg-secondary transition-colors"
                            >
                                Visit mythosindiastudio.com <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>
                        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
                            <div className="rounded-lg border border-primary bg-primary/5 p-6" data-testid="parent-lifevault">
                                <BookHeart className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-4 font-display text-lg font-semibold">LifeVault</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    AI life-OS — wealth, heritage, sanctuary mode. Live in production. Series-A ready.
                                </p>
                                <div className="mt-4 label-tag text-primary">Live · Q2 2026</div>
                            </div>
                            <div className="rounded-lg border border-border bg-card p-6" data-testid="parent-edusphere">
                                <Library className="h-6 w-6 text-primary" strokeWidth={1.5} />
                                <h3 className="mt-4 font-display text-lg font-semibold">EduSphere</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    Mythology-led STEM, gamified curriculum, multilingual classrooms — for the same households.
                                </p>
                                <div className="mt-4 label-tag">In private build</div>
                            </div>
                            <div className="sm:col-span-2 rounded-lg border border-dashed border-border p-6">
                                <Target className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                                <h3 className="mt-4 font-display text-lg font-semibold">The Mythos OS thesis</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    A single AI infrastructure powering wealth (LifeVault), learning (EduSphere), and
                                    eventually identity & devotion. Indian-rooted, globally scalable, defensible by data
                                    graph and brand. We're not building a product — we're building the household OS.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* INVESTOR CTA STRIP */}
            <section className="py-16 border-y border-border/70 bg-gradient-to-r from-primary/15 via-primary/5 to-transparent">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 grid md:grid-cols-12 gap-6 items-center">
                    <div className="md:col-span-8">
                        <div className="label-tag mb-2">Investors & Partners</div>
                        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                            We're raising to compound the data graph & ship EduSphere.
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                            Open to lead and select co-investors. Strategic capital from family-office,
                            wealth-management, or AI-first funds preferred.
                        </p>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Link
                            to="/investors"
                            data-testid="cta-investors"
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 rounded-full text-sm font-semibold hover:bg-primary/90"
                        >
                            Read the investor brief <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="py-20 lg:py-28">
                <div className="mx-auto max-w-5xl px-6 sm:px-8 text-center">
                    <div className="label-tag mb-3 justify-center inline-flex">Pricing</div>
                    <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
                        Free for families. ARPU from advisors.
                    </h2>
                    <div className="mt-12 grid md:grid-cols-2 gap-6 text-left">
                        <div className="rounded-2xl border border-border bg-card p-8">
                            <div className="label-tag">Family</div>
                            <div className="mt-3 font-display text-5xl font-bold">Free</div>
                            <p className="mt-3 text-sm text-muted-foreground">Vault, archive, sanctuary mode, AI for one household.</p>
                            <Link to="/register" className="mt-7 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-full text-sm font-semibold">
                                Begin your vault <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="relative rounded-2xl border border-primary bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 overflow-hidden">
                            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                            <div className="text-xs uppercase tracking-[0.28em] font-semibold opacity-90 relative">Advisor</div>
                            <div className="mt-3 font-display text-5xl font-bold relative">₹500–2,000</div>
                            <p className="mt-3 text-sm opacity-90 relative">per client / year — unlimited modules + advisor console.</p>
                            <Link to="/register" className="mt-7 inline-flex items-center gap-2 bg-background text-foreground px-5 py-3 rounded-full text-sm font-semibold hover:bg-background/90 relative">
                                Start as advisor <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="py-10 text-sm text-muted-foreground border-t border-border/70" data-testid="landing-footer">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <BrandLockup size="sm" parent={true} />
                    <div className="text-xs text-center md:text-right leading-relaxed">
                        © {new Date().getFullYear()}{" "}
                        <a
                            href="https://mythosindiastudio.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-mythos-link"
                            className="underline hover:text-foreground"
                        >
                            Mythos India Studios Private Limited ↗
                        </a>
                        . LifeVault is a trademark of MYTHIVA™.
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground/80">
                            CIN U62010TS2026PTC221089
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                        <a
                            href="https://mythosindiastudio.com/contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-contact-link"
                            className="underline hover:text-foreground"
                        >
                            Contact ↗
                        </a>
                        <Link to="/investors" className="underline hover:text-foreground">Investor brief →</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
